/**
 * Portfolio locks: which Brand Portfolio / release features the AGENCY package lacks.
 *
 * Source: `lockedFeatures` (+ optional `lockedFeatureUpsell`) of GET /api/agency/portfolio, read ONCE by
 * <PortfolioLocksProvider> from the page's cached query. Leaf components read the provider via
 * usePortfolioLocks() instead of each subscribing to the query. The list is empty unless the backend enforces,
 * so an empty list = nothing changes visually. It deliberately does NOT read team-scoped entitlements: team-less
 * staff, staff with a home team and God Mode all see the AGENCY state here.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { usePortfolioClients } from '@/hooks/usePortfolio';
import { useGatingStore } from '@/stores/gating.store';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

export interface PortfolioLocks {
  /** The agency package lacks the feature (and the backend enforces). */
  isLocked: (featureKey: string) => boolean;
  /** The lock list is known (portfolio response arrived, or the request failed: then nothing is locked). */
  decided: boolean;
}

const NOTHING_LOCKED: PortfolioLocks = { isLocked: () => false, decided: true };

const PortfolioLocksContext = createContext<PortfolioLocks>(NOTHING_LOCKED);

export const PortfolioLocksProvider = ({ children }: { children: ReactNode }) => {
  const { data, isError } = usePortfolioClients();
  const recordLocked = useGatingStore((state) => state.recordLocked);
  // resetForTeamChange wipes the learned slugs: re-seed them whenever the effective team changes
  const { teamId } = useEffectiveTeamId();
  const lockedFeatures = data?.lockedFeatures;
  const upsell = data?.lockedFeatureUpsell;

  const locks = useMemo<PortfolioLocks>(() => {
    const set = new Set(lockedFeatures ?? []);
    return { isLocked: (featureKey) => set.has(featureKey), decided: data !== undefined || isError };
  }, [lockedFeatures, data, isError]);

  // Teach the locked cards the cheapest plan per key (plan name + CTA)
  useEffect(() => {
    for (const key of lockedFeatures ?? []) {
      const slug = upsell?.[key];
      if (slug) recordLocked(key, slug);
    }
  }, [lockedFeatures, upsell, recordLocked, teamId]);

  return <PortfolioLocksContext.Provider value={locks}>{children}</PortfolioLocksContext.Provider>;
};

export const usePortfolioLocks = (): PortfolioLocks => useContext(PortfolioLocksContext);

export default usePortfolioLocks;
